import { useCallback, useEffect, useState } from "react";
import {
  BadgeCheck,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SkeletonListRow } from "@/components/Skeleton";
import { AdminCommandCenter } from "@/components/admin/AdminCommandCenter";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminQueue() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [confirming, setConfirming] = useState(null); // { kind: 'reject'|'purge', broker }
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pending-brokers");
      setBrokers(data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load pending brokers"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const verify = async (brokerId) => {
    setVerifyingId(brokerId);
    try {
      await api.post(`/admin/verify-broker/${brokerId}`);
      toast.success("Broker verified. They can now access their dashboard.");
      setBrokers((prev) => prev.filter((b) => b.id !== brokerId));
      window.dispatchEvent(new CustomEvent("admin:metrics-refresh"));
    } catch (err) {
      toast.error(formatApiError(err, "Verification failed"));
    } finally {
      setVerifyingId(null);
    }
  };

  const doReject = async () => {
    if (!confirming) return;
    const id = confirming.broker.id;
    setBusyId(id);
    try {
      await api.post(`/admin/reject-broker/${id}`, { reason: rejectReason });
      toast.success("Broker application rejected.");
      setBrokers((prev) => prev.filter((b) => b.id !== id));
      window.dispatchEvent(new CustomEvent("admin:metrics-refresh"));
    } catch (err) {
      toast.error(formatApiError(err, "Reject failed"));
    } finally {
      setBusyId(null);
      setConfirming(null);
      setRejectReason("");
    }
  };

  const doPurge = async () => {
    if (!confirming) return;
    const id = confirming.broker.id;
    setBusyId(id);
    try {
      await api.delete(`/admin/purge-broker/${id}`);
      toast.success("Broker record permanently deleted.");
      setBrokers((prev) => prev.filter((b) => b.id !== id));
      window.dispatchEvent(new CustomEvent("admin:metrics-refresh"));
    } catch (err) {
      toast.error(formatApiError(err, "Purge failed"));
    } finally {
      setBusyId(null);
      setConfirming(null);
    }
  };

  return (
    <div className="mb-page flex min-h-screen flex-col" data-testid="admin-queue">
      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-12 md:px-10 md:py-16">
        <div className="mb-rise">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            <ShieldCheck className="h-3.5 w-3.5" />
            Admin command center
          </div>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Business at a glance
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-500">
            Live network KPIs, growth signals, and every broker awaiting
            verification — all in one place.
          </p>
        </div>

        <div className="mt-10">
          <AdminCommandCenter />
        </div>

        <div className="mt-16 border-t border-gray-100 pt-10" data-testid="admin-queue-section">
          <div className="mb-rise">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#35622F]">
              <ShieldCheck className="h-3.5 w-3.5" />
              Verification queue
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Pending broker verifications
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500">
              Review each broker&apos;s PRC ID and license number. Verified
              brokers immediately unlock their dashboard.
            </p>
          </div>

          <div className="mt-6 flex items-center justify-between">
          <div className="text-sm font-medium text-gray-500" data-testid="admin-queue-count">
            {loading ? "Loading…" : `${brokers.length} broker${brokers.length === 1 ? "" : "s"} awaiting review`}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={load}
            className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="mt-8 space-y-4" data-testid="admin-queue-loading-skeleton">
            <SkeletonListRow />
            <SkeletonListRow />
            <SkeletonListRow />
          </div>
        ) : brokers.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#35622F]/10">
              <BadgeCheck className="h-6 w-6 text-[#35622F]" />
            </div>
            <h3 className="mt-6 text-xl font-semibold text-gray-900">All caught up.</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              There are no brokers waiting for verification right now.
            </p>
          </div>
        ) : (
          <ul className="mt-8 grid grid-cols-1 gap-5">
            {brokers.map((b) => (
              <li
                key={b.id}
                className="grid grid-cols-1 gap-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card md:grid-cols-[160px,1fr,auto] md:items-center md:p-7"
                data-testid={`pending-broker-row-${b.id}`}
              >
                <button
                  type="button"
                  className="group relative h-32 w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50 md:h-32 md:w-40"
                  onClick={() => setPreviewSrc(b.prc_id_url)}
                  data-testid={`prc-id-thumb-${b.id}`}
                >
                  {b.prc_id_url ? (
                    <img
                      src={b.prc_id_url}
                      alt={`PRC ID of ${b.full_name}`}
                      className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                      No image
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/40 via-black/0 to-transparent opacity-0 transition group-hover:opacity-100">
                    <span className="mb-2 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-gray-800">
                      View full ID
                    </span>
                  </div>
                </button>

                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Broker
                  </div>
                  <div
                    className="mt-1 text-lg font-semibold text-gray-900"
                    data-testid={`broker-name-${b.id}`}
                  >
                    {b.full_name}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                    <span data-testid={`broker-email-${b.id}`}>{b.email}</span>
                    {b.phone && (
                      <>
                        <span className="hidden text-gray-300 sm:inline">·</span>
                        <span data-testid={`broker-phone-${b.id}`}>{b.phone}</span>
                      </>
                    )}
                    <span className="hidden text-gray-300 sm:inline">·</span>
                    <span>
                      PRC license{" "}
                      <span
                        className="font-semibold text-gray-800"
                        data-testid={`broker-license-${b.id}`}
                      >
                        {b.prc_license_number}
                      </span>
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Submitted {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="flex flex-col gap-2 md:items-end">
                  <Button
                    type="button"
                    onClick={() => verify(b.id)}
                    disabled={verifyingId === b.id || busyId === b.id}
                    className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
                    data-testid={`verify-broker-button-${b.id}`}
                  >
                    {verifyingId === b.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      <>
                        <BadgeCheck className="mr-2 h-4 w-4" />
                        Verify broker
                      </>
                    )}
                  </Button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRejectReason("");
                        setConfirming({ kind: "reject", broker: b });
                      }}
                      disabled={verifyingId === b.id || busyId === b.id}
                      className="mb-focus-ring inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 text-xs font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                      data-testid={`reject-broker-button-${b.id}`}
                    >
                      <ShieldX className="h-3.5 w-3.5" />
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirming({ kind: "purge", broker: b })
                      }
                      disabled={verifyingId === b.id || busyId === b.id}
                      className="mb-focus-ring inline-flex h-9 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                      data-testid={`purge-broker-button-${b.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        </div>
      </main>

      {previewSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6 backdrop-blur"
          onClick={() => setPreviewSrc(null)}
          data-testid="prc-id-preview-overlay"
        >
          <img
            src={previewSrc}
            alt="PRC ID full view"
            className="max-h-[90vh] max-w-4xl rounded-2xl border border-white/20 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <AlertDialog
        open={confirming?.kind === "reject"}
        onOpenChange={(o) => !o && setConfirming(null)}
      >
        <AlertDialogContent
          className="rounded-2xl bg-white"
          data-testid="reject-broker-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-gray-900">
              Reject this broker application?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              {confirming?.broker
                ? `${confirming.broker.full_name} will no longer see this app until you re-approve them. This is reversible.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="reject-reason"
              className="text-xs font-semibold uppercase tracking-wider text-gray-500"
            >
              Reason (optional, shown internally)
            </label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={280}
              className="mb-focus-ring w-full rounded-xl border border-gray-200 bg-white p-3 text-sm"
              placeholder="e.g. PRC ID unreadable, license number does not match the PRC roster…"
              data-testid="reject-broker-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-xl border-gray-200 bg-white text-gray-700"
              data-testid="reject-broker-dialog-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doReject}
              className="rounded-xl bg-amber-600 text-white hover:bg-amber-700"
              data-testid="reject-broker-dialog-confirm"
            >
              Reject application
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirming?.kind === "purge"}
        onOpenChange={(o) => !o && setConfirming(null)}
      >
        <AlertDialogContent
          className="rounded-2xl bg-white"
          data-testid="purge-broker-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-gray-900">
              Permanently delete this broker record?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              {confirming?.broker
                ? `This will hard-delete ${confirming.broker.full_name}'s account and their uploaded PRC ID. Only non-verified brokers can be purged. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-xl border-gray-200 bg-white text-gray-700"
              data-testid="purge-broker-dialog-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doPurge}
              className="rounded-xl bg-red-600 text-white hover:bg-red-700"
              data-testid="purge-broker-dialog-confirm"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
